import React from 'react'
import { 
  Add,
  Psychology,
  TrendingUp
} from '@mui/icons-material'
import { Typography } from '../../../interfaces/typography'
import { PrimaryActionButton } from '../../../utils/CommonUIComponents'

interface EmptyStateProps {
  onAddMetric: () => void
}

export function EmptyState({ onAddMetric }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-8 text-center min-h-[600px]">
      {/* Icon illustration */}
      <div className="relative mb-8">
        <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center mb-4 mx-auto shadow-sm">
          <Psychology sx={{ fontSize: 40, color: '#3B82F6' }} />
        </div>
        <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
          <TrendingUp sx={{ fontSize: 16, color: '#059669' }} />
        </div>
      </div>

      {/* Main heading */}
      <Typography variant="h1" color="#1F2937" fontWeight={700} className="mb-4 text-3xl">
        Create Your First Custom Metric
      </Typography>

      {/* Description */}
      <Typography variant="largeParagraph" color="#6B7280" className="mb-8 max-w-2xl leading-relaxed">
        Custom metrics let you have AI grade conversations and calls however you'd like. 
        Create metrics to measure anything from objection handling to rapport building, 
        with AI providing consistent, objective scores based on your specific criteria.
      </Typography>

      {/* Feature highlights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10 max-w-4xl">
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4 mx-auto">
            <Psychology sx={{ fontSize: 24, color: '#3B82F6' }} />
          </div>
          <Typography variant="h4" color="#1F2937" fontWeight={600} className="mb-2">
            AI-Powered Scoring
          </Typography>
          <Typography variant="smallParagraph" color="#6B7280">
            Let AI analyze conversations and provide consistent, objective scores
          </Typography>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center mb-4 mx-auto">
            <TrendingUp sx={{ fontSize: 24, color: '#059669' }} />
          </div>
          <Typography variant="h4" color="#1F2937" fontWeight={600} className="mb-2">
            Custom Criteria
          </Typography>
          <Typography variant="smallParagraph" color="#6B7280">
            Define exactly what you want to measure with your own scoring criteria
          </Typography>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center mb-4 mx-auto">
            <div className="w-6 h-6 bg-purple-500 rounded text-white flex items-center justify-center text-xs font-bold">
              %
            </div>
          </div>
          <Typography variant="h4" color="#1F2937" fontWeight={600} className="mb-2">
            Multiple Formats
          </Typography>
          <Typography variant="smallParagraph" color="#6B7280">
            Support for percentages, yes/no, categories, and custom scoring
          </Typography>
        </div>
      </div>

      {/* CTA button */}
      <PrimaryActionButton
        icon={<Add sx={{ fontSize: 24 }} />}
        onClick={onAddMetric}
        className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
      >
        Create Your First Custom Metric
      </PrimaryActionButton>

      {/* Help text */}
      <Typography variant="smallParagraph" color="#9CA3AF" className="mt-6">
        Or choose from our pre-made templates on the right →
      </Typography>
    </div>
  )
} 